'use client';

import {
  Building2,
  Compass,
  Globe,
  Heart,
  Plane as PlaneIcon,
  Zap,
} from 'lucide-react';

import { IndexRouteSchema } from '@/components/index-route-schema';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';

const industries = [
  {
    titleKey: 'Financial Services' as const,
    descriptionKey:
      'Banks, fintech, and credit unions. Handle account inquiries, fraud reports, and payment questions with compliant AI that customers trust.' as const,
    href: '/industries/financial-services',
    icon: Building2,
  },
  {
    titleKey: 'Healthcare' as const,
    descriptionKey:
      'Hospitals, clinics, and telehealth. Automate appointment scheduling, insurance verification, and prescription refills while protecting patient privacy.' as const,
    href: '/industries/healthcare',
    icon: Heart,
  },
  {
    titleKey: 'Mobility & Transportation' as const,
    descriptionKey:
      'Ride-sharing, car rentals, and gig economy. Support riders and drivers at scale with instant trip dispute resolution and refund automation.' as const,
    href: '/industries/mobility',
    icon: PlaneIcon,
  },
  {
    titleKey: 'Technology & SaaS' as const,
    descriptionKey:
      'Software platforms, marketplaces, and B2B SaaS. Help users succeed with onboarding, troubleshooting, and API support for complex products.' as const,
    href: '/industries/technology',
    icon: Zap,
  },
  {
    titleKey: 'Travel & Hospitality' as const,
    descriptionKey:
      'Tour operators, RV rentals, and activity bookings. Handle reservations, weather cancellations, and equipment questions across 100+ languages.' as const,
    href: '/industries/travel-hospitality',
    icon: Globe,
  },
  {
    titleKey: 'Tours & Activity Operators' as const,
    descriptionKey:
      'Cruises, dives, adventures, and experiences. Answer bookings, reschedules, and weather cancellations 24/7 in 100+ languages—so your crew stays in the field.' as const,
    href: '/industries/tour-operators',
    icon: Compass,
  },
];

export default function IndustriesPage() {
  const { t } = useTranslation();

  return (
    <>
      <IndexRouteSchema route="/industries" />
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="py-32">
          <div className="container">
            <div className="mx-auto max-w-3xl text-center">
              <h1 className="mb-6 font-serif text-[2rem] md:text-4xl xl:text-5xl">
                {t.industries['AI customer support']} <br />{' '}
                {t.industries['for your industry']}
              </h1>
              <p className="text-muted-foreground text-lg md:text-xl">
                {
                  t.industries[
                    'Every industry has unique support needs. Agent 5 adapts to yours—handling the specific questions, compliance requirements, and workflows that matter most.'
                  ]
                }
              </p>
            </div>
          </div>
        </section>

        {/* Industries Grid */}
        <section className="pb-32">
          <div className="container">
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {industries.map((industry) => (
                <Link
                  key={industry.href}
                  href={industry.href}
                  className="group border-border hover:border-primary/50 relative flex flex-col rounded-2xl border p-8 transition-all hover:shadow-lg"
                >
                  <div className="mb-6 flex items-center gap-4">
                    <div className="bg-muted group-hover:bg-primary/10 flex size-12 items-center justify-center rounded-lg transition-colors">
                      <industry.icon className="group-hover:text-primary size-6 transition-colors" />
                    </div>
                    <h2 className="group-hover:text-primary text-2xl transition-colors">
                      {t.industries[industry.titleKey]}
                    </h2>
                  </div>

                  <p className="text-muted-foreground mb-4 flex-1">
                    {t.industries[industry.descriptionKey]}
                  </p>

                  <div className="text-primary mt-4 flex items-center gap-2 text-sm font-medium">
                    {t.industries['Learn more →']}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-mint-50 py-32">
          <div className="container">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="mb-4 font-serif text-3xl sm:text-4xl">
                {t.industries["Don't see your industry?"]}
              </h2>
              <p className="text-muted-foreground mb-8 text-lg">
                {
                  t.industries[
                    'Agent 5 works across industries. If you have customers who need support, we can help.'
                  ]
                }
              </p>
              <Link
                href="/about-open#contact"
                prefetch={true}
                className="inline-flex items-center justify-center rounded-full bg-black px-8 py-3 text-base font-medium text-white transition-colors hover:bg-neutral-900"
              >
                {t.industries['Talk to us about your industry']}
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
